<script setup lang="ts">
import { computed, ref } from "vue"
import { Flash, InlineAlert } from "@xy-planning-network/trees"
import { isValidEmail } from "@/composables/email"
import Footer from "@/components/shared/Footer.vue"
import LoginAPI from "@/api/login"

// refs
const email = ref("")
const errorText = ref("")
const loading = ref(false)
const manualLogin = ref(false)
const manualLoginSuccess = ref(false)

const next = new URLSearchParams(window.location.search).get("next") || ""

// methods
const create = (): void => {
  loading.value = true
  LoginAPI.createMagicLogin(email.value, next).then(
    () => {
      loading.value = false
      manualLoginSuccess.value = true
    },
    () => {
      errorText.value = "Email login for that address is not available."
      loading.value = false
    }
  )
}

const onEmailChange = (): void => {
  if (errorText.value !== "") {
    errorText.value = ""
  }
}

// computed
const validEmail = computed((): boolean => {
  return isValidEmail(email.value)
})
</script>

<template>
  <div class="relative min-h-screen">
    <Flash />

    <div class="bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div class="hidden sm:block sm:absolute sm:inset-y-0 sm:h-full sm:w-full">
        <div class="relative h-full max-w-screen-xl mx-auto">
          <img
            class="absolute opacity-5 right-full transform translate-y-1/2 translate-x-1/4 lg:translate-x-1/2"
            src="@/assets/XYPN_Grid_Blue-9.svg"
          />
          <img
            class="absolute opacity-10 left-full transform -translate-y-3/4 -translate-x-1/4 md:-translate-y-1/2 lg:-translate-x-1/3"
            src="@/assets/XYPN_Grid_Green-2.svg"
          />
        </div>
      </div>

      <div class="sm:mx-auto sm:w-full sm:max-w-md">
        <img class="mx-auto h-40 w-auto" src="@/assets/logo-wide.svg" />
        <h1 class="xy-h1-extra-flair text-center">Sign in to your account</h1>
      </div>

      <div
        v-if="!manualLogin"
        class="mt-4 sm:mx-auto sm:w-full sm:max-w-md relative"
      >
        <a
          :href="
            next ? `/auth/google/login?next=${next}` : '/auth/google/login'
          "
        >
          <img
            src="@/assets/btn_google_signin_light_normal_web.svg"
            class="mt-4 mx-auto w-[223px]"
          />
        </a>

        <a
          :href="
            next
              ? `/auth/microsoft/login?next=${next}`
              : '/auth/microsoft/login'
          "
        >
          <img
            src="@/assets/ms-symbollockup_signin_light.svg"
            class="mt-2 mx-auto"
          />
        </a>

        <p class="mt-2 text-center max-w">
          Or
          <a href="#" class="xy-link" @click.prevent="manualLogin = true">
            continue with email&hellip;
          </a>
        </p>
      </div>

      <div
        v-if="manualLogin"
        class="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative"
      >
        <div
          v-if="!manualLoginSuccess"
          class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10"
        >
          <form @submit.prevent="create()">
            <div>
              <InlineAlert
                v-if="errorText"
                :content="errorText"
                class="mb-2"
                kind="alert"
              />
              <label
                for="email"
                class="block text-sm font-medium leading-5 text-gray-700"
              >
                Email address
              </label>
              <div class="mt-1 rounded-md shadow-sm relative">
                <BaseInput
                  v-model="email"
                  type="email"
                  @input="onEmailChange"
                />
              </div>
            </div>

            <div class="mt-6">
              <span class="block w-full">
                <button
                  type="submit"
                  class="xy-btn w-full"
                  :disabled="!validEmail || loading"
                >
                  Send Magic Link
                </button>
              </span>
            </div>
          </form>
        </div>

        <div
          v-else
          class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 mx-auto"
        >
          <div class="flex-1 bg-white p-6 flex flex-col justify-between">
            <img src="@/assets/icons/send.svg" class="mb-2" />
            <a href="#" class="block">
              <h3 class="mt-2 font-semibold">
                We've sent an email to {{ email }}.
              </h3>
              <p class="mt-3 text-base leading-6 text-gray-600">
                Please note: the magic link provided in the email will expire in
                5 minutes.
              </p>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Footer bg="bg-gray-50"></Footer>
</template>
